import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import React, { memo, useEffect, useState } from 'react';
import { ParamsProps } from 'src/app/common/components/ParamsProvider';
import { Translation } from 'src/app/i18n';
import styles from './password-modal.module.css';

type ComponentProps = ParamsProps & {
  title?: string;
  inputLabel?: string;
  open: boolean;
  onClose: () => void;
  onConfirm: (password: string, confirmPassword: string) => void;
};

const dialogStyle = {
  margin: 10,
  display: 'flex',
  justifyContent: 'flex-end',
};

export const PasswordModalComponent: React.FC<ComponentProps> = memo((props: ComponentProps) => {
  const { title, open, onClose, onConfirm } = props;
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  useEffect(() => {
    if (open) {
      setPassword('');
      setConfirmPassword('');
      setShowPassword(false);
      setShowConfirmPassword(false);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent className="tw-grid tw-grid-cols-[auto_1fr] tw-items-center tw-gap-y-4 tw-gap-x-6">
        <span className="tw-text-base tw-whitespace-nowrap">{Translation('new_file_password')}</span>
        <TextField
          margin="dense"
          variant="outlined"
          label={Translation('new_file_password')}
          fullWidth
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          type={showPassword ? 'text' : 'password'}
          className={styles.pwInputWithCustomIcon}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <span className="tw-text-base tw-whitespace-nowrap">{Translation('confirm_file_password')}</span>
        <TextField
          margin="dense"
          variant="outlined"
          label={Translation('confirm_file_password')}
          fullWidth
          autoComplete="current-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          type={showConfirmPassword ? 'text' : 'password'}
          className={styles.pwInputWithCustomIcon}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </DialogContent>

      <DialogActions style={dialogStyle}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          {Translation('global.text.cancel')}
        </Button>

        <Button
          onClick={() => {
            onConfirm(password, confirmPassword);
          }}
          disabled={!(password && confirmPassword && password === confirmPassword)}
          variant="contained"
          color="secondary"
        >
          {Translation('global.text.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
});
